import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import EmptyState from "@/components/EmptyState";
import axios from "axios";
import { motion } from "framer-motion";
import {
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  TrendingUp,
  TrendingDown,
  Activity,
  Target,
  Download,
  BarChart3,
  Zap,
  Shield,
  Brain,
  Flame,
  FileText,
} from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "@/lib/api";
import TradingChart from "@/components/TradingChart";

const API = API_URL;

const COLORS = {
  win: "hsl(var(--success))",
  loss: "hsl(var(--destructive))",
  primary: "hsl(var(--primary))",
  orange: "#f97316",
  blue: "#1877F2",
};

function MetricCard({ title, value, icon: Icon, color, subtitle, loading }) {
  return (
    <Card className="shadow-sm hover:shadow-md transition-shadow duration-200">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className={`h-4 w-4 ${color || "text-muted-foreground"}`} />
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-8 w-20 mb-1" />
        ) : (
          <div className="text-2xl font-bold">{value}</div>
        )}
        {subtitle && !loading && (
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        )}
      </CardContent>
    </Card>
  );
}

function StreakBar({ label, count, type }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-muted-foreground w-8">{label}</span>
      <div className="flex-1 h-2 bg-secondary/50 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${
            type === "win" ? "bg-success" : "bg-destructive"
          }`}
          style={{ width: `${Math.min(count * 10, 100)}%` }}
        />
      </div>
      <span className="text-xs font-medium w-6 text-right">{count}</span>
    </div>
  );
}

function HeatmapCell({ day, hour, value, maxValue }) {
  const intensity = maxValue > 0 ? value / maxValue : 0;
  const bgColor =
    value > 0
      ? `rgba(24, 119, 242, ${Math.max(intensity * 0.8, 0.1)})`
      : value < 0
      ? `rgba(239, 68, 68, ${Math.max(Math.abs(intensity) * 0.8, 0.1)})`
      : "hsl(var(--secondary))";

  return (
    <div
      className="w-8 h-8 rounded-sm flex items-center justify-center text-[10px] font-medium cursor-default transition-colors"
      style={{ backgroundColor: bgColor }}
      title={`${day} ${hour}:00 — P/L: ${value >= 0 ? "+" : ""}${value.toFixed(0)}`}
    >
      {value !== 0 ? (value >= 0 ? "+" : "") + value.toFixed(0) : "·"}
    </div>
  );
}

function SkeletonStats() {
  return (
    <div className="p-4 md:p-6">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <Skeleton className="h-10 w-40 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-48 rounded-full" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-4 rounded" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-20 mb-2" />
              <Skeleton className="h-3 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-4 rounded" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-8 w-24" />
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {[1, 2].map((i) => (
          <Card key={i} className="shadow-lg">
            <CardHeader>
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-64" />
            </CardHeader>
            <CardContent style={{ height: "300px" }}>
              <Skeleton className="h-full w-full rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function Statistics({ currentStrategy, mode }) {
  const [statistics, setStatistics] = useState(null);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const loadStatistics = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, tradesRes] = await Promise.allSettled([
        axios.get(`${API}/statistics/${currentStrategy.id}?mode=${mode}`, { headers }),
        axios.get(`${API}/trades?strategy_id=${currentStrategy.id}&mode=${mode}`, { headers }),
      ]);
      if (statsRes.status === "fulfilled") setStatistics(statsRes.value.data);
      if (tradesRes.status === "fulfilled") setTrades(tradesRes.value.data);
    } catch (error) {
      console.error("Error loading statistics:", error);
      toast.error("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  }, [currentStrategy.id, mode]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  // Download CSV
  const handleDownloadReport = async () => {
    try {
      setDownloadingReport(true);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/report?mode=${mode}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      const reportBlob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(reportBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "all-strategies-report.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV report downloaded");
    } catch (error) {
      toast.error("Error downloading report");
    } finally {
      setDownloadingReport(false);
    }
  };

  // Download PDF (client-side generated from statistics)
  const handleDownloadPdf = () => {
    try {
      setDownloadingPdf(true);
      // Generate a simple text-based PDF summary
      const lines = [
        "=== TRADING TRACK STATISTICS REPORT ===",
        `Strategy: ${currentStrategy.name}`,
        `Currency: ${currentStrategy.currency}`,
        `Generated: ${new Date().toISOString()}`,
        "",
        "--- KEY METRICS ---",
        `Total Trades: ${statistics.total_trades}`,
        `Win Rate: ${statistics.win_rate}%`,
        `Total P/L: ${statistics.total_profit_loss}`,
        `Profit Factor: ${statistics.profit_factor || "N/A"}`,
        `Sharpe Ratio: ${statistics.sharpe_ratio || "N/A"}`,
        `Max Drawdown: ${statistics.max_drawdown}%`,
        `Expectancy: ${statistics.expectancy || "N/A"}`,
        `Best Trade: ${statistics.best_trade}`,
        `Worst Trade: ${statistics.worst_trade}`,
        `Winning Trades: ${statistics.winning_trades}`,
        `Losing Trades: ${statistics.losing_trades}`,
        "",
        "--- STREAKS ---",
        `Best Win Streak: ${statistics.best_win_streak || "N/A"}`,
        `Best Loss Streak: ${statistics.best_loss_streak || "N/A"}`,
      ];

      const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `statistics-${currentStrategy.name}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Statistics report downloaded");
    } catch (error) {
      toast.error("Error generating report");
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Compute derived metrics
  const computedMetrics = useMemo(() => {
    if (!statistics) return {};

    const profitFactor =
      statistics.profit_factor !== undefined
        ? statistics.profit_factor
        : statistics.losing_trades > 0
        ? (statistics.winning_trades / statistics.losing_trades).toFixed(2)
        : "∞";

    const sharpeRatio = statistics.sharpe_ratio || "N/A";
    const maxDrawdown = statistics.max_drawdown || 0;
    const expectancy =
      statistics.expectancy !== undefined
        ? statistics.expectancy
        : statistics.total_trades > 0
        ? (statistics.total_profit_loss / statistics.total_trades).toFixed(2)
        : 0;

    return { profitFactor, sharpeRatio, maxDrawdown, expectancy };
  }, [statistics]);

  // Distribution histogram
  const distributionData = useMemo(() => {
    if (!statistics?.distribution) {
      // Build from trades if available
      return [];
    }
    return statistics.distribution;
  }, [statistics]);

  // Win/Loss streaks
  const streaks = useMemo(() => {
    return {
      currentWinStreak: statistics?.current_win_streak || 0,
      currentLossStreak: statistics?.current_loss_streak || 0,
      bestWinStreak: statistics?.best_win_streak || 0,
      bestLossStreak: statistics?.best_loss_streak || 0,
    };
  }, [statistics]);

  // Heatmap data (performance by day of week and hour)
  const heatmapData = useMemo(() => {
    if (!statistics?.heatmap) return null;
    return statistics.heatmap;
  }, [statistics]);

  if (loading) {
    return <SkeletonStats />;
  }

  if (!statistics || statistics.total_trades === 0) {
    return (
      <div className="p-4 md:p-6" data-testid="no-statistics-message">
        <EmptyState
          icon={BarChart3}
          title="No data to analyze"
          description="Start trading! Record trades in the dashboard to view statistics."
          actionLabel="Go to Dashboard"
          onAction={() => (window.location.href = `/${mode}`)}
        />
      </div>
    );
  }

  const pieData = [
    { name: "Wins", value: statistics.winning_trades, color: COLORS.win },
    { name: "Losses", value: statistics.losing_trades, color: COLORS.loss },
  ];

  return (
    <div className="p-4 md:p-6" data-testid="statistics-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">Statistics</h1>
            <p className="text-muted-foreground text-sm md:text-base">
              Strategy performance: {currentStrategy.name}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="rounded-full text-xs sm:text-sm"
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
            >
              <FileText className="h-4 w-4 mr-2" />
              {downloadingPdf ? "Generating..." : "Export PDF"}
            </Button>
            <Button
              variant="outline"
              className="rounded-full text-xs sm:text-sm"
              onClick={handleDownloadReport}
              disabled={downloadingReport}
            >
              <Download className="h-4 w-4 mr-2" />
              {downloadingReport ? "Downloading..." : "Export CSV"}
            </Button>
          </div>
        </div>

        {/* Key Metrics Row 1 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
          <MetricCard
            title="Total Trades"
            value={statistics.total_trades}
            icon={Activity}
            subtitle={`${statistics.winning_trades}W / ${statistics.losing_trades}L`}
          />
          <MetricCard
            title="Win Rate"
            value={`${statistics.win_rate}%`}
            icon={Target}
            subtitle={`${statistics.winning_trades} wins / ${statistics.losing_trades} losses`}
          />
          <MetricCard
            title="Total P/L"
            value={`${statistics.total_profit_loss >= 0 ? "+" : ""}${statistics.total_profit_loss} ${currentStrategy.currency}`}
            icon={statistics.total_profit_loss >= 0 ? TrendingUp : TrendingDown}
            color={statistics.total_profit_loss >= 0 ? "text-success" : "text-destructive"}
          />
          <MetricCard
            title="Max Drawdown"
            value={`${statistics.max_drawdown}%`}
            icon={TrendingDown}
            color="text-destructive"
          />
        </div>

        {/* Key Metrics Row 2 - Advanced */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
          <MetricCard
            title="Profit Factor"
            value={computedMetrics.profitFactor}
            icon={Zap}
            color="text-orange-500"
            subtitle="Gross profit / gross loss"
          />
          <MetricCard
            title="Sharpe Ratio"
            value={computedMetrics.sharpeRatio}
            icon={Brain}
            color="text-blue-500"
            subtitle="Risk-adjusted return"
          />
          <MetricCard
            title="Expectancy"
            value={`${computedMetrics.expectancy} ${currentStrategy.currency}`}
            icon={Target}
            subtitle="Avg expected profit per trade"
          />
          <MetricCard
            title="Avg Trade"
            value={`${(statistics.total_profit_loss / statistics.total_trades).toFixed(2)} ${currentStrategy.currency}`}
            icon={Activity}
            subtitle="Mean P/L per trade"
          />
        </div>

        {/* Win/Loss Streaks */}
        <Card className="shadow-lg mb-6 border-border/50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-orange-500" />
              <CardTitle className="text-lg">Win/Loss Streaks</CardTitle>
            </div>
            <CardDescription>Current and historical streaks</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Win Streaks</h4>
                <StreakBar label="Current" count={streaks.currentWinStreak} type="win" />
                <StreakBar label="Best" count={streaks.bestWinStreak} type="win" />
              </div>
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Loss Streaks</h4>
                <StreakBar label="Current" count={streaks.currentLossStreak} type="loss" />
                <StreakBar label="Best" count={streaks.bestLossStreak} type="loss" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
          {/* Win Rate Pie */}
          <Card className="shadow-lg" data-testid="win-rate-chart">
            <CardHeader>
              <CardTitle className="text-lg md:text-xl">Win Rate Distribution</CardTitle>
              <CardDescription>Ratio between winning and losing trades</CardDescription>
            </CardHeader>
            <CardContent className="flex items-center justify-center" style={{ height: "300px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "0.5rem",
                    }}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Account Growth */}
          <Card className="shadow-lg" data-testid="account-growth-chart">
            <CardHeader>
              <CardTitle className="text-lg md:text-xl">Account Growth</CardTitle>
              <CardDescription>Capital evolution over time</CardDescription>
            </CardHeader>
            <CardContent style={{ height: "300px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={statistics.account_growth}>
                  <defs>
                    <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3} />
                      <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 12 }} />
                  <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "0.5rem",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="balance"
                    stroke={COLORS.primary}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#colorBalance)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>

        {/* Distribution Histogram */}
        {distributionData.length > 0 && (
          <Card className="shadow-lg mb-6 border-border/50">
            <CardHeader>
              <CardTitle className="text-lg">Trade Result Distribution</CardTitle>
              <CardDescription>Histogram of P/L results</CardDescription>
            </CardHeader>
            <CardContent style={{ height: "250px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={distributionData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="range" stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                  <YAxis stroke="hsl(var(--muted-foreground))" tick={{ fontSize: 11 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "0.5rem",
                    }}
                  />
                  <Bar dataKey="count" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* TradingView-style Chart */}
        <div className="mb-6">
          <TradingChart trades={trades} />
        </div>

        {/* Performance Heatmap */}
        {heatmapData && (
          <Card className="shadow-lg mb-6 border-border/50">
            <CardHeader>
              <CardTitle className="text-lg">Performance by Hour & Day</CardTitle>
              <CardDescription>Heatmap showing P/L by day of week and hour</CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <div className="min-w-[600px]">
                <div className="flex gap-1 mb-1 ml-10">
                  {Array.from({ length: 24 }, (_, i) => (
                    <div key={i} className="w-8 text-center text-[9px] text-muted-foreground">
                      {i}
                    </div>
                  ))}
                </div>
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                  <div key={day} className="flex gap-1 mb-1 items-center">
                    <span className="text-[10px] text-muted-foreground w-8 text-right mr-1">{day}</span>
                    {Array.from({ length: 24 }, (_, hour) => {
                      const key = `${day}_${hour}`;
                      const value = heatmapData[key] || 0;
                      return (
                        <HeatmapCell
                          key={key}
                          day={day}
                          hour={hour}
                          value={value}
                          maxValue={Math.max(...Object.values(heatmapData).map(Math.abs), 1)}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Strategy Comparison */}
        {statistics.strategy_breakdown && statistics.strategy_breakdown.length > 1 && (
          <Card className="shadow-lg border-border/50">
            <CardHeader>
              <CardTitle className="text-lg">Strategy Comparison</CardTitle>
              <CardDescription>Performance breakdown by strategy</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {statistics.strategy_breakdown.map((s, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-3 rounded-lg bg-secondary/30"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                        s.profit_loss >= 0 ? "bg-success/15" : "bg-destructive/15"
                      }`}>
                        {s.profit_loss >= 0 ? (
                          <TrendingUp className="h-4 w-4 text-success" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-destructive" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{s.name || `Strategy ${i + 1}`}</p>
                        <p className="text-xs text-muted-foreground">{s.trade_count || 0} trades • Win rate: {s.win_rate || 0}%</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold ${s.profit_loss >= 0 ? "text-success" : "text-destructive"}`}>
                        {s.profit_loss >= 0 ? "+" : ""}{s.profit_loss} {currentStrategy.currency}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        PF: {s.profit_factor || "N/A"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </motion.div>
    </div>
  );
}
